import React from 'react';
import { 
  Container, 
  Box, 
  Typography, 
  Paper,
  Divider,
  Breadcrumbs,
  Link
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

const TermsPage: React.FC = () => {
  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 4 }}>
        <Link component={RouterLink} to="/" color="inherit">
          Home
        </Link>
        <Typography color="text.primary">Terms and Conditions</Typography>
      </Breadcrumbs>
      
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          Terms and Conditions
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Last Updated: March 7, 2025
        </Typography>
      </Box>
      
      {/* Content */}
      <Paper 
        elevation={0} 
        className="glass-container"
        sx={{ 
          p: 4,
          borderRadius: 4
        }}
      >
        <Typography variant="h5" component="h2" gutterBottom>
          1. Introduction
        </Typography>
        <Typography variant="body1" paragraph>
          Welcome to SharkPanzer ("we," "our," or "us"). SharkPanzer is a product of UnwiringTech®. Unwiring Tech GSTIN- 09CYNPA1063Q1ZZ. By accessing or using our website, mobile application, or any other services we offer (collectively, the "Services"), you agree to be bound by these Terms of Service ("Terms"). Please read these Terms carefully before using our Services.
        </Typography>
        <Typography variant="body1" paragraph>
          By accessing or using our Services, you agree to be bound by these Terms. If you do not agree with our policies and practices, we recommend you refrain from using our Services.
        </Typography>
        
        <Divider sx={{ my: 4 }} />
        
        <Typography variant="h5" component="h2" gutterBottom>
          2. Description of Services
        </Typography>
        <Typography variant="body1" paragraph>
          SharkPanzer is an AI-powered platform that creates comprehensive knowledge capsules from YouTube videos. Our Services include, but are not limited to:
        </Typography>
        <Typography component="ul" sx={{ pl: 4 }}>
          <li>
            <Typography variant="body1" paragraph>
              Processing YouTube videos to extract and organize information
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              Creating various output formats such as Full Capsules, Key Insights, Mind Maps, and more
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              Providing AI-powered chat functionality to interact with video content
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              Offering multi-language support for content generation
            </Typography>
          </li>
        </Typography>
        
        <Divider sx={{ my: 4 }} />
        
        <Typography variant="h5" component="h2" gutterBottom>
          3. User Accounts
        </Typography>
        <Typography variant="body1" paragraph>
          To access certain features of our Services, you may need to create an account. When you create an account, you agree to provide accurate, current, and complete information. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.
        </Typography>
        <Typography variant="body1" paragraph>
          You agree to notify us immediately of any unauthorized use of your account or any other breach of security. We cannot and will not be liable for any loss or damage arising from your failure to comply with these requirements.
        </Typography>
        
        <Divider sx={{ my: 4 }} />
        
        <Typography variant="h5" component="h2" gutterBottom>
          4. Subscription Plans and Payments
        </Typography>
        <Typography variant="body1" paragraph>
          We offer various subscription plans, including a free tier with limited features and paid tiers with additional features and higher usage limits. By subscribing to a paid plan, you agree to pay all fees associated with your subscription.
        </Typography>
        <Typography variant="body1" paragraph>
          All payments are processed through secure third-party payment processors. We do not store your payment information on our servers. By providing your payment information, you authorize us to charge your payment method for the subscription plan you have selected.
        </Typography>
        <Typography variant="body1" paragraph>
          Subscription fees are billed in advance on a monthly or yearly basis, depending on the billing cycle you select. Your subscription will automatically renew at the end of each billing cycle unless you cancel it before the renewal date.
        </Typography>
        
        <Divider sx={{ my: 4 }} />
        
        <Typography variant="h5" component="h2" gutterBottom>
          5. Cancellation and Refunds
        </Typography>
        <Typography variant="body1" paragraph>
          You can cancel your subscription at any time through your account settings. If you cancel your subscription, you will continue to have access to your paid features until the end of your current billing cycle.
        </Typography>
        <Typography variant="body1" paragraph>
          We offer a 3-day money-back guarantee for all paid plans, provided you have used less than 10% of your subscription credits. If you are not satisfied with our Services, you can request a refund within 3 days of your initial purchase. To request a refund, please contact our support team at support@unwiringtech.com.
        </Typography>
        <Typography variant="body1" paragraph>
        Refunds outside the 3-day window may be considered in exceptional circumstances. Please contact our support team for more information.
        </Typography>
        
        <Divider sx={{ my: 4 }} />
        
        <Typography variant="h5" component="h2" gutterBottom>
          6. User Content and Conduct
        </Typography>
        <Typography variant="body1" paragraph>
          Our Services may allow you to upload, submit, store, send, or receive content, including but not limited to text, images, and videos ("User Content"). You retain ownership of any intellectual property rights that you hold in your User Content.
        </Typography>
        <Typography variant="body1" paragraph>
          By uploading, submitting, storing, sending, or receiving User Content through our Services, you grant us a worldwide, non-exclusive, royalty-free license to use, host, store, reproduce, modify, create derivative works, communicate, publish, publicly perform, publicly display, and distribute such User Content for the limited purpose of providing and improving our Services.
        </Typography>
        <Typography variant="body1" paragraph>
          You agree not to use our Services to:
        </Typography>
        <Typography component="ul" sx={{ pl: 4 }}>
          <li>
            <Typography variant="body1" paragraph>
              Violate any applicable laws or regulations
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              Infringe upon the intellectual property rights of others
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              Upload, transmit, or distribute any content that is unlawful, harmful, threatening, abusive, harassing, defamatory, vulgar, obscene, or otherwise objectionable
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              Interfere with or disrupt the integrity or performance of our Services
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              Attempt to gain unauthorized access to our Services or related systems or networks
            </Typography>
          </li>
        </Typography>
        
        <Divider sx={{ my: 4 }} />
        
        <Typography variant="h5" component="h2" gutterBottom>
          7. Intellectual Property
        </Typography>
        <Typography variant="body1" paragraph>
          Our Services and all content and materials available through our Services, including but not limited to text, graphics, logos, icons, images, audio clips, and software, are the property of SharkPanzer or our licensors and are protected by copyright, trademark, and other intellectual property laws.
        </Typography>
        <Typography variant="body1" paragraph>
          You may not use, reproduce, distribute, modify, create derivative works of, publicly display, publicly perform, republish, download, store, or transmit any of the material on our Services, except as follows:
        </Typography>
        <Typography component="ul" sx={{ pl: 4 }}>
          <li>
            <Typography variant="body1" paragraph>
              Your computer may temporarily store copies of such materials in RAM incidental to your accessing and viewing those materials
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              You may store files that are automatically cached by your web browser for display enhancement purposes
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              You may print or download one copy of a reasonable number of pages of the website for your own personal, non-commercial use and not for further reproduction, publication, or distribution
            </Typography>
          </li>
        </Typography>
        
        <Divider sx={{ my: 4 }} />
        
        <Typography variant="h5" component="h2" gutterBottom>
          8. Limitation of Liability
        </Typography>
        <Typography variant="body1" paragraph>
        To the extent permitted by law, SharkPanzer and its affiliates will not be responsible for any indirect, incidental, or special damages, including loss of profits, data, goodwill, or other intangible losses arising from your use of, or inability to use, the Services.
        </Typography>
        <Typography variant="body1" paragraph>
          To the maximum extent permitted by law, our liability for damages shall be limited to the amount you paid for the Services in the 12 months preceding the claim. The limitations of damages set forth above are fundamental elements of the basis of the bargain between SharkPanzer and you.
        </Typography>
        
        <Divider sx={{ my: 4 }} />
        
        <Typography variant="h5" component="h2" gutterBottom>
          9. Disclaimer of Warranties
        </Typography>
        <Typography variant="body1" paragraph>
        While we strive to provide high-quality Services, you agree to use our Services at your own discretion and risk. The Services are provided on an "AS IS" and "AS AVAILABLE" basis. SharkPanzer expressly disclaims all warranties of any kind, whether express or implied, including but not limited to the implied warranties of merchantability, fitness for a particular purpose, and non-infringement.
        </Typography>
        <Typography variant="body1" paragraph>
          SharkPanzer makes no warranty that the Services will meet your requirements, be available on an uninterrupted, secure, or error-free basis, or be accurate, reliable, complete, legal, or safe.
        </Typography>
        
        <Divider sx={{ my: 4 }} />
        
        <Typography variant="h5" component="h2" gutterBottom>
          10. Indemnification
        </Typography>
        <Typography variant="body1" paragraph>
          You agree to defend, indemnify, and hold harmless SharkPanzer, its affiliates, licensors, and service providers, and its and their respective officers, directors, employees, contractors, agents, licensors, suppliers, successors, and assigns from and against any claims, liabilities, damages, judgments, awards, losses, costs, expenses, or fees (including reasonable attorneys' fees) arising out of or relating to your violation of these Terms or your use of the Services.
        </Typography>
        
        <Divider sx={{ my: 4 }} />
        
        <Typography variant="h5" component="h2" gutterBottom>
          11. Governing Law and Jurisdiction
        </Typography>
        <Typography variant="body1" paragraph>
          These Terms and your use of the Services shall be governed by and construed in accordance with the laws of Agra, Uttar Pradesh, India, without giving effect to any choice or conflict of law provision or rule. For international users, any disputes will also be handled under the laws of India, respecting applicable international regulations.
        </Typography>
        <Typography variant="body1" paragraph>
          Any legal suit, action, or proceeding arising out of, or related to, these Terms or the Services shall be instituted exclusively in the courts of Agra, Uttar Pradesh, India. You waive any and all objections to the exercise of jurisdiction over you by such courts and to venue in such courts. Any disputes for international users will also be governed by the laws of Agra, Uttar Pradesh, India.
        </Typography>
        
        <Divider sx={{ my: 4 }} />
        
        <Typography variant="h5" component="h2" gutterBottom>
          12. Changes to Terms
        </Typography>
        <Typography variant="body1" paragraph>
          We may revise these Terms from time to time at our sole discretion. All changes are effective immediately when we post them, and apply to all access to and use of the Services thereafter.
        </Typography>
        <Typography variant="body1" paragraph>
        We encourage you to review these Terms periodically to stay informed of any updates. Your continued use of the Services indicates acceptance of any changes.
        </Typography>
        
        <Divider sx={{ my: 4 }} />
        
        <Typography variant="h5" component="h2" gutterBottom>
          13. Termination
        </Typography>
        <Typography variant="body1" paragraph>
        We reserve the right to suspend or terminate your account if we believe you have violated these Terms. Whenever possible, we will provide notice prior to taking such action.
        </Typography>
        <Typography variant="body1" paragraph>
          Upon termination, your right to use the Services will immediately cease. If you wish to terminate your account, you may simply discontinue using the Services or contact us to request account deletion.
        </Typography>
        
        <Divider sx={{ my: 4 }} />
        
        <Typography variant="h5" component="h2" gutterBottom>
          14. Contact Information
        </Typography>
        <Typography variant="body1" paragraph>
        We appreciate your support and are always here to help if you have any questions about these Terms. Feel free to reach out to us at:
        </Typography>
        <Typography variant="body1" paragraph>
          Email: support@unwiringtech.com
        </Typography>
      </Paper>
    </Container>
  );
};

export default TermsPage;
